import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';

// Auto-dismiss durations (ms) per toast type.
const TOAST_DURATIONS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // Track pending timeouts so they can be cleared on unmount/dismiss.
  const timeoutsRef = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
  }, []);

  // addToast accepts optional `taskId` and `actionType` metadata for dedup.
  // When a new toast shares the same taskId (both non-null) OR the same
  // actionType (both non-null) as an existing toast, the older toast is
  // removed (replace, not stack). The visible stack is also capped at 3 —
  // the oldest toasts are dismissed when the cap is exceeded. Both the
  // replaced and the capped-away toasts have their pending auto-dismiss
  // timeout cleared so it can't fire later on a toast that's no longer shown.
  const addToast = useCallback((type, message, duration = TOAST_DURATIONS[type] || 3000, taskId = null, actionType = null) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const toast = { id, type, message, duration, visible: true, taskId, actionType };

    setToasts((prev) => {
      // Replace: drop any existing toast that shares the same taskId (both
      // non-null) OR the same actionType (both non-null).
      const survivors = prev.filter((t) => {
        const sameTask = taskId && t.taskId && t.taskId === taskId;
        const sameAction = actionType && t.actionType && t.actionType === actionType;
        if (sameTask || sameAction) {
          if (timeoutsRef.current[t.id]) {
            clearTimeout(timeoutsRef.current[t.id]);
            delete timeoutsRef.current[t.id];
          }
          return false;
        }
        return true;
      });
      // Cap the visible stack at 3: dismiss the oldest (front) toasts.
      let next = [...survivors, toast];
      if (next.length > 3) {
        const overflow = next.length - 3;
        for (let i = 0; i < overflow; i++) {
          if (timeoutsRef.current[next[i].id]) {
            clearTimeout(timeoutsRef.current[next[i].id]);
            delete timeoutsRef.current[next[i].id];
          }
        }
        next = next.slice(overflow);
      }
      return next;
    });

    timeoutsRef.current[id] = setTimeout(() => {
      dismissToast(id);
    }, duration);

    return id;
  }, [dismissToast]);

  const toastSuccess = useCallback((message, taskId = null, actionType = null) => addToast('success', message, undefined, taskId, actionType), [addToast]);
  const toastError = useCallback((message, taskId = null, actionType = null) => addToast('error', message, undefined, taskId, actionType), [addToast]);
  const toastWarning = useCallback((message, taskId = null, actionType = null) => addToast('warning', message, undefined, taskId, actionType), [addToast]);
  const toastInfo = useCallback((message, taskId = null, actionType = null) => addToast('info', message, undefined, taskId, actionType), [addToast]);

  const value = useMemo(
    () => ({
      toasts,
      toastSuccess,
      toastError,
      toastWarning,
      toastInfo,
      dismissToast,
    }),
    [toasts, toastSuccess, toastError, toastWarning, toastInfo, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};
